import React, { useState, useEffect, useCallback, useRef } from 'react';
import { VERGUNNING_CATEGORIEEN, MELDING_DOELEN, MELDING_DOEL_TOELICHTING,
         labelVoorDoel, doelVoluit, categorieenVanDoelen } from '../util/doelen';
import { useParams, useNavigate, useSearchParams, Link, Outlet } from 'react-router-dom';
import { getInrichtingDetail, wijzigInrichting, beeindigInrichting, getInrichtingHistorie, getGebieden, verstuurBijwerkingsmail, getEmailBijwerkingStatus, getProductieStatus, verstuurProductieLink, getVeldwerkBulkStatus, verstuurVeldwerkBulkLink } from '../api/api';
import VerversPill from '../components/VerversPill';
import { usePresence, PresenceLabel } from '../components/usePresence';
import { useAuth } from '../components/AuthContext';
import { fmtId, fmtPeriode, kenmerkTypeLabel, fmtDatumNL, dieptetekst } from '../util/format';
import Modal from '../components/Modal';
import Klokje from '../components/Klokje';
import KaartMetLegenda from '../components/KaartMetLegenda';
import GrondwaterproductieDossier from '../components/GrondwaterproductieDossier';
import OpmerkingenVeld from '../components/OpmerkingenVeld';
import CorrespondentieModal from '../components/CorrespondentieModal';
import { updateInrichtingOpmerkingen } from '../api/api';

function Field({ label, value, editing, onChange, type = 'text', options }) {
  if (!editing) {
    let display = value;
    if (options && value) {
      const found = options.find(o => o.value === value);
      if (found) display = found.label;
    }
    // Een datumveld is alleen bij het bewerken een ISO-datum; lezen doe je in
    // dezelfde notatie als overal elders.
    if (type === 'date' && value) display = fmtDatumNL(value);
    return (
      <div className="field">
        <span className="field-label">{label}</span>
        <span className={`field-value ${!value ? 'empty' : ''}`}>{display || '—'}</span>
      </div>
    );
  }
  if (options) {
    return (
      <div className="field">
        <span className="field-label">{label}</span>
        <select value={value || ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    );
  }
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      {type === 'textarea' ? (
        <textarea rows={3} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

// === dode melding-vervang-code verwijderd (PutRijHandmatig -> NieuweMeldingModal) ===
export default function InrichtingDetail({ historieModus = false }) {
  const { id, kenmerk } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [sliceMeta, setSliceMeta] = useState(null);  // historie-modus: reden/wie/periode van het recht
  const [editing, setEditing] = useState(false);
  /* ?wijzigingsbesluit=1 — de keuzemodal op de houderpagina stuurt je hierheen
     als er al een vergunning-inrichting is. Dan gaan we meteen door naar de
     verwerkpagina; anders zou je zelf nog een keer door het actiemenu moeten. */
  const [zoek] = useSearchParams();
  const [mailBezig, setMailBezig] = useState(false);
  const [mailDialogOpen, setMailDialogOpen] = useState(false);
  const [mailResult, setMailResult] = useState(null);
  const [mailStatus, setMailStatus] = useState(null);  // { verzonden_op, actueel } of null
  const [prodOpen, setProdOpen] = useState(false);
  const [prodJaar, setProdJaar] = useState(new Date().getFullYear() - 1);
  const [prodStatus, setProdStatus] = useState(null);   // { ontbrekende_kwartalen, heeft_email } of null
  const [prodBezig, setProdBezig] = useState(false);
  const [prodResult, setProdResult] = useState(null);
  const [vwbOpen, setVwbOpen] = useState(false);
  const [vwbStatus, setVwbStatus] = useState(null);   // { aantal_openstaand, putten, heeft_email, lopende_link } of null
  const [vwbBezig, setVwbBezig] = useState(false);
  const [vwbResult, setVwbResult] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [beeindigOpen, setBeeindigOpen] = useState(false);
  const [beeindigDatum, setBeeindigDatum] = useState(new Date().toISOString().slice(0, 10));
  const [publiekOpen, setPubliekOpen] = useState(false);
  const [actiesOpen, setActiesOpen] = useState(false);
  const actiesRef = useRef(null);
  const [historie, setHistorie] = useState([]);
  const [historieOpen, setHistorieOpen] = useState(false);

  const [gebieden, setGebieden] = useState([]);

  useEffect(() => {
    if (zoek.get('wijzigingsbesluit') !== '1') return;
    navigate(`/beregening/inrichtingen/${id}/wijzigingsbesluit`, { replace: true });
  }, [zoek, navigate, id]);

  const laad = useCallback(async () => {
    try {
      const [detail, hist, alleGeb] = await Promise.all([
        getInrichtingDetail(id),
        getInrichtingHistorie(id),
        getGebieden(undefined, true),
      ]);
      setGebieden(alleGeb.data);

      if (historieModus) {
        // Bouw uit de gekozen gebruiksrecht-slice een data-object met exact
        // dezelfde vorm als de live detail, zodat dezelfde render het toont.
        const slice = (hist.data || []).find(s => s.kenmerk === decodeURIComponent(kenmerk));
        if (!slice) {
          setError('Dit gebruiksrecht is niet gevonden in de historie.');
          return;
        }
        const sliceInrichting = {
          inrichting_id: id,
          houder_id: detail.data.houder?.houder_id,
          verzoeknummer: slice.kenmerk,
          gebruiksrecht_kenmerk: slice.kenmerk,
          gebruiksrecht_kenmerk_type: slice.kenmerk_type,
          wijzigingsbesluitnummer: slice.wijzigingsbesluitnummer,
          rechtstype: slice.rechtstype,
          ingangsdatum: slice.inwerkingtreding,
          einddatum: slice.uitwerkingtreding,  // gezet → isActief=false → toont alle putten met periode
          meldingsdatum: slice.meldingsdatum,
          inwerkingtreding: slice.inwerkingtreding,
          uitwerkingtreding: slice.uitwerkingtreding,
          doel: null,
          doelen: slice.doelen,
          vergund_debiet_uur: slice.vergund_debiet_uur,
          vergund_debiet_maand: slice.vergund_debiet_maand,
          vergund_debiet_kwartaal: slice.vergund_debiet_kwartaal,
          vergund_debiet_jaar: slice.vergund_debiet_jaar,
          max_pompcap_m3_uur: slice.max_pompcap_m3_uur,
          bedrijfsomvang_ha: slice.bedrijfsomvang_ha,
          opmerkingen: null,
        };
        setData({
          inrichting: sliceInrichting,
          houder: detail.data.houder,
          putten: (slice.putten || []).map(p => ({ ...p, actief: false })),
          geplande_wijzigingen: [],
        });
        setForm(sliceInrichting);  // het detailblok bindt aan form → toont de slice, niet de live inrichting
        setHistorie([]);  // geen Gebruiksrechten-card binnen de historie-weergave
        setSliceMeta({
          kenmerk: slice.kenmerk,
          kenmerk_type: slice.kenmerk_type,
          inwerkingtreding: slice.inwerkingtreding,
          uitwerkingtreding: slice.uitwerkingtreding,
          reden: slice.reden,
          gewijzigd_door: slice.gewijzigd_door,
          gearchiveerd_op: slice.gearchiveerd_op,
        });
        return;
      }

      setData(detail.data);
      setForm(detail.data.inrichting);
      setHistorie(hist.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  }, [id, kenmerk, historieModus]);

  useEffect(() => { laad(); }, [laad]);

  // Lichte refresh: alleen detail + historie opnieuw ophalen (niet de zware
  // gebieden-overlay, die is bij openen geladen en verandert zelden).
  const lichteRefresh = useCallback(async () => {
    const [detail, hist] = await Promise.all([
      getInrichtingDetail(id),
      getInrichtingHistorie(id),
    ]);
    setData(detail.data);
    setForm(detail.data.inrichting);
    setHistorie(hist.data);
  }, [id]);

  // Presence: niet in historie-weergave (dan kijk je naar een archief-slice).
  const { gebruiker } = useAuth();
  const presenceAnderen = usePresence('inrichting', historieModus ? null : id, editing, gebruiker?.naam);

  // Acties-menu sluiten bij klik buiten het menu of bij Escape.
  useEffect(() => {
    if (!actiesOpen) return;
    const opKlik = (e) => {
      if (actiesRef.current && !actiesRef.current.contains(e.target)) setActiesOpen(false);
    };
    const opToets = (e) => { if (e.key === 'Escape') setActiesOpen(false); };
    document.addEventListener('mousedown', opKlik);
    document.addEventListener('keydown', opToets);
    return () => {
      document.removeEventListener('mousedown', opKlik);
      document.removeEventListener('keydown', opToets);
    };
  }, [actiesOpen]);

  useEffect(() => {
    let alive = true;
    async function fetchMailStatus() {
      try {
        const status = await getEmailBijwerkingStatus(id);
        if (alive) setMailStatus(status);
      } catch (e) {
        if (alive) setMailStatus({ verzonden_op: null, actueel: false });
      }
    }
    fetchMailStatus();
    return () => { alive = false; };
  }, [id, mailResult]);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const verstuurMail = async () => {
    setMailBezig(true);
    setMailDialogOpen(false);
    try {
      const result = await verstuurBijwerkingsmail(id);
      setMailResult({ ok: true, ...result });
    } catch (e) {
      setMailResult({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setMailBezig(false);
    }
  };

  // Productie-status ophalen wanneer de dialoog open is of het jaar wijzigt
  useEffect(() => {
    if (!prodOpen) return;
    let alive = true;
    setProdStatus(null);
    getProductieStatus(id, prodJaar)
      .then((res) => { if (alive) setProdStatus(res.data); })
      .catch(() => { if (alive) setProdStatus({ ontbrekende_kwartalen: [], heeft_email: false, _fout: true }); });
    return () => { alive = false; };
  }, [prodOpen, prodJaar, id]);

  const verstuurProductie = async () => {
    setProdBezig(true);
    setProdResult(null);
    try {
      const res = await verstuurProductieLink(id, prodJaar);
      setProdOpen(false);
      setProdResult({ ok: true, ...res.data });
    } catch (e) {
      setProdResult({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setProdBezig(false);
    }
  };

  // Openstaande putten ophalen wanneer de dialoog opengaat
  useEffect(() => {
    if (!vwbOpen) return;
    let alive = true;
    setVwbStatus(null);
    getVeldwerkBulkStatus(id)
      .then((res) => { if (alive) setVwbStatus(res.data); })
      .catch(() => { if (alive) setVwbStatus({ aantal_openstaand: 0, putten: [], heeft_email: false, _fout: true }); });
    return () => { alive = false; };
  }, [vwbOpen, id]);

  const verstuurVeldwerkBulk = async () => {
    setVwbBezig(true);
    setVwbResult(null);
    try {
      const res = await verstuurVeldwerkBulkLink(id);
      setVwbOpen(false);
      setVwbResult({ ok: true, ...res.data });
    } catch (e) {
      setVwbResult({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setVwbBezig(false);
    }
  };

  const opslaan = async () => {
    setBezig(true);
    setError(null);
    try {
      const payload = { ...form };
      delete payload.inrichting_id;
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      ['max_pompcap_m3_uur', 'bedrijfsomvang_ha', 'vergund_debiet_uur', 'vergund_debiet_maand', 'vergund_debiet_kwartaal', 'vergund_debiet_jaar'].forEach(k => {
        if (payload[k] !== null) payload[k] = parseFloat(payload[k]);
      });
      payload.verwachte_gewijzigd_op = form.gewijzigd_op || null;
      delete payload.gewijzigd_op;
      delete payload.aangemaakt_op;
      await wijzigInrichting(id, payload);
      setEditing(false);
      laad();
    } catch (e) {
      if (e.response?.status === 409) {
        setEditing(false);
        setError('Een collega heeft deze inrichting zojuist gewijzigd. De pagina is opnieuw geladen met de actuele gegevens.');
        lichteRefresh();
      } else {
        setError(e.response?.data?.detail || e.message);
      }
    } finally {
      setBezig(false);
    }
  };

  const beeindigen = async () => {
    try {
      await beeindigInrichting(id, beeindigDatum);
      setBeeindigOpen(false);
      laad();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

// === dode melding-handlers verwijderd (-> NieuweMeldingModal) ===

    if (!data) return <div className="empty-state"><span className="spinner" /></div>;

  const {
    inrichting, houder, putten,
    geplande_wijzigingen: geplandeWijzigingen = [],
    geplande_meldingen: geplandeMeldingen = [],
  } = data;
  const isActief = !inrichting.einddatum;

  /* Alles wat klaarstaat maar nog niet geldt, in één lijst voor de tijdlijn:
     klaarstaande meldingen én een gepland wijzigingsbesluit. De tijdlijn loopt
     van nieuw naar oud, dus het verst weg gelegen moment staat bovenaan. */
  const klaarstaand = [
    ...geplandeMeldingen.map((m) => ({
      soort: 'melding',
      sleutel: `m-${m.gepland_id}`,
      gepland_id: m.gepland_id,
      datum: m.inwerkingtreding,
      kenmerk: m.kenmerk,
      kenmerk_type: m.kenmerk_type,
    })),
    ...geplandeWijzigingen.map((g) => ({
      soort: 'wijziging',
      sleutel: `wb-${g.gepland_id}`,
      gepland_id: g.gepland_id,
      datum: g.ingangsdatum,
      kenmerk: g.wijzigingsbesluitnummer,
      kenmerk_type: 'wijzigingsbesluit',
    })),
  ].sort((a, b) => String(b.datum || '').localeCompare(String(a.datum || '')));

  const vandaag = new Date().toISOString().slice(0, 10);
  /* Een recht dat al vastligt maar pas later ingaat. Dan hoort de tijdlijn er
     ook te staan als er verder niets in de historie zit: "Gaat in op" is juist
     dán het enige wat dit onderscheidt van een lopend recht. */
  const nogNietIngegaanRecht = historie.some(
    (s) => s.is_huidig && s.inwerkingtreding && s.inwerkingtreding > vandaag
  );

  /* Eén recht tegelijk.

       lopend recht      wat daaronder valt
       nog geen lopend   het eerstvolgende, want anders staat er vier weken
                         lang niets op de pagina van een nieuwe melding
       beëindigd         de historische koppelingen; daar is de historie het
                         verhaal en komt er niets meer bij

     Nooit twee momenten door elkaar: welk moment je ziet staat boven de lijst,
     en wat er daarna komt lees je in de tijdlijn. */
  const actievePutten = putten.filter(p => p.actief);
  const toontToekomst = isActief && actievePutten.length === 0
    && putten.some(p => p.aankomend_vanaf);
  const zichtbarePutten = !isActief
    ? putten.filter(p => !p.aankomend_vanaf)
    : toontToekomst
      ? putten.filter(p => p.aankomend_vanaf)
      : actievePutten;
  /* De datum waarop de getoonde lijst geldt, als dat niet vandaag is. */
  const geldtVanaf = toontToekomst
    ? zichtbarePutten.map(p => p.aankomend_vanaf).sort()[0]
    : null;
  /* Het eerstvolgende dat klaarstaat — voor het klokje bij de titel. De lijst
     `klaarstaand` loopt van ver weg naar dichtbij, dus dat is de laatste. */
  const eerstvolgend = klaarstaand.length ? klaarstaand[klaarstaand.length - 1] : null;
  const naarKlaarstaand = (k) => navigate(k.soort === 'melding'
    ? `/beregening/inrichtingen/${id}/gepland-melding/${k.gepland_id}`
    : `/beregening/inrichtingen/${id}/gepland-besluit/${k.gepland_id}`);

  return (
    <div className={`main-inner${historieModus ? ' historie-modus' : ''}`}>
      {historieModus && (
        <Link to={`/beregening/inrichtingen/${id}`} className="historie-terug" style={{ marginBottom: 14 }}>
          ← Terug naar inrichting {fmtId(id)}
        </Link>
      )}
      <div className={historieModus ? 'detail-enkel' : 'detail-layout'}>
        <div className="detail-header page-header">
        <div>
          <h1 className="page-title">
            {/* Er staat iets klaar. Eén teken, met de datum in de tooltip en een
                klik naar het venster — wie het wil weten kijkt, wie het niet
                nodig heeft leest er overheen. */}
            {!historieModus && eerstvolgend && (
              <button
                type="button"
                className="titel-klok"
                onClick={() => naarKlaarstaand(eerstvolgend)}
                title={eerstvolgend.soort === 'melding'
                  ? `Er staat een melding klaar (${eerstvolgend.kenmerk || '—'}) die ingaat op ${fmtDatumNL(eerstvolgend.datum)}`
                  : `Er staat een wijzigingsbesluit klaar (${eerstvolgend.kenmerk || '—'}) dat ingaat op ${fmtDatumNL(eerstvolgend.datum)}`}
              >
                <Klokje grootte={22} titel="Er staat iets klaar" />
              </button>
            )}
            {(inrichting.inrichting_id || '').replace(/-/g, '')}
          </h1>
          <p className="page-subtitle">
            {historieModus && sliceMeta
              ? <>{kenmerkTypeLabel(sliceMeta.kenmerk_type)} {sliceMeta.kenmerk} · geldig {fmtPeriode(sliceMeta.inwerkingtreding, sliceMeta.uitwerkingtreding)}</>
              : <>{inrichting.rechtstype}{!isActief && ` · beëindigd per ${fmtDatumNL(inrichting.einddatum)}`}</>}
          </p>
        </div>
        {!historieModus && (
        <div className="row">
          <PresenceLabel anderen={presenceAnderen} />
          {!editing && !historieModus && <VerversPill onVervers={lichteRefresh} />}
          {!editing ? (
            <div className="acties-menu-wrap" ref={actiesRef} style={{ position: 'relative', display: 'inline-block' }}>
              <button className="btn btn-secondary" onClick={() => setActiesOpen(o => !o)}>
                Acties
                <svg
                  width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  style={{ marginLeft: 2, transition: 'transform .15s', transform: actiesOpen ? 'rotate(180deg)' : 'none' }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {actiesOpen && (
                <div className="acties-popover">
                  {/* Alles wat naar de melder gaat: de link naar zijn publieke
                      pagina en het Melding-document. Staat er ook bij een
                      beeindigde inrichting: die pagina blijft bestaan en de
                      melder kan er nog naar vragen. */}
                  <button
                    className="acties-item"
                    onClick={() => { setActiesOpen(false); setPubliekOpen(true); }}
                    title="De link naar de pagina van de melder, en het document bij de melding"
                  >
                    Correspondentie
                  </button>
                  {isActief && houder?.emailadres && (() => {
                    const reedsBevestigd = mailStatus?.verzonden_op && mailStatus?.actueel;
                    const verzondenDatum = mailStatus?.verzonden_op
                      ? fmtDatumNL(mailStatus.verzonden_op)
                      : null;
                    return (
                      <button
                        className="acties-item"
                        onClick={() => { setActiesOpen(false); setMailResult(null); setMailDialogOpen(true); }}
                        disabled={mailBezig || reedsBevestigd}
                        title={reedsBevestigd ? 'Bijwerking is bevestigd. Wijzig de inrichting om een nieuwe bevestiging te kunnen versturen.' : `Verstuurt naar ${houder?.emailadres}`}
                      >
                        {mailBezig
                          ? 'Versturen…'
                          : reedsBevestigd
                            ? `Bijwerking bevestigd op ${verzondenDatum}`
                            : 'Bijwerking bevestigen'}
                      </button>
                    );
                  })()}
                  <button className="acties-item" onClick={() => { setActiesOpen(false); setEditing(true); }}>
                    Correctie doorvoeren
                  </button>
                  {isActief && houder?.emailadres && (
                    <button
                      className="acties-item"
                      onClick={() => { setActiesOpen(false); setProdResult(null); setProdOpen(true); }}
                      title={`Verstuurt naar ${houder?.emailadres}`}
                    >
                      Productieopgave opvragen
                    </button>
                  )}
                  {isActief && houder?.emailadres && (
                    <button
                      className="acties-item"
                      onClick={() => { setActiesOpen(false); setVwbResult(null); setVwbOpen(true); }}
                      title={`Verstuurt naar ${houder?.emailadres}`}
                    >
                      Veldwerkgegevens opvragen
                    </button>
                  )}
                  {/* Ook als er al eentje klaarstaat: besluiten stapelen, ze
                      vervangen elkaar niet. De backend bewaakt dat het nieuwe
                      besluit ná het laatste geplande ingaat. */}
                  {inrichting.rechtstype === 'vergunning' && isActief && (
                    <button className="acties-item" onClick={() => { setActiesOpen(false); navigate(`/beregening/inrichtingen/${id}/wijzigingsbesluit`); }}>
                      Wijzigingsbesluit verwerken
                    </button>
                  )}
                  {inrichting.rechtstype === 'melding' && isActief && (
                    <button className="acties-item" onClick={() => { setActiesOpen(false); navigate(`/beregening/inrichtingen/${id}/melding-verwerken`); }}>
                      Nieuwe melding verwerken
                    </button>
                  )}
                  {isActief && (
                    <>
                      <div className="acties-divider" />
                      <button
                        className="acties-item acties-item-danger"
                        onClick={() => { setActiesOpen(false); setBeeindigOpen(true); }}
                      >
                        Beëindigen
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
              <button className="btn btn-ghost" onClick={() => { setEditing(false); setForm(inrichting); }}>Annuleren</button>
              <button className="btn btn-primary" onClick={opslaan} disabled={bezig}>
                {bezig ? <><span className="spinner" /> Opslaan...</> : 'Opslaan'}
              </button>
            </>
          )}
        </div>
        )}
      </div>

        <div className="detail-main">

      {error && <div className="alert alert-error">{error}</div>}


      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Putten</h3>
          <span className="chip">{zichtbarePutten.length}</span>
          {/* Welk moment je ziet. Alleen nodig als dat niet vandaag is: bij een
              inrichting waarvan het recht nog moet ingaan. */}
          {geldtVanaf && (
            <span className="chip chip-toekomstig" style={{ marginLeft: 8 }}>
              <Klokje grootte={12} />Geldt vanaf{' '}
              <span className="chip-datum">{fmtDatumNL(geldtVanaf)}</span>
            </span>
          )}
        </div>
        {zichtbarePutten.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 24 }}>
            Geen putten gekoppeld.
          </div>
        ) : (
          <>
            <KaartMetLegenda
              punten={zichtbarePutten.map(p => ({
                put_id: p.put_id,
                x_rd: p.x_rd,
                y_rd: p.y_rd,
                perceel: p.perceel,
                // Oranje: moet nog geboord worden. Dezelfde marker die het
                // meldingsformulier voor zo'n put gebruikt, dus dezelfde taal.
                // Stond eerder op "komt er nog bij", maar dat is een uitspraak
                // over de koppeling en niet over de put.
                soort: p.boorstatus_code === 'te_boren' ? 'nieuw' : undefined,
                onClick: () => navigate(`/beregening/putten/${p.put_id}`),
              }))}
              overlays={gebieden}
              hoogte={320}
              /* De putnummers naast de markers, zoals op de publieke pagina.
                 Hier wel en op het puttenoverzicht niet: daar staan er honderden
                 op de kaart en wordt het een muur van tekst. */
              toonPutnummers
            />
            <div style={{ marginTop: 16 }} />
            <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Coördinaten (RD)</th>
                <th>Boorstatus</th>
                <th>Diepte</th>
                <th>Filterdiepte (bovenkant)</th>
                <th>Perceel</th>
                <th>{geldtVanaf ? 'Vanaf' : 'Periode'}</th>
              </tr>
            </thead>
            <tbody>
              {zichtbarePutten.map(p => (
                <tr key={`${p.put_id}-${p.begin_datum_koppeling || p.aankomend_vanaf}`} className="clickable" onClick={() => navigate(`/beregening/putten/${p.put_id}`)}>
                  <td><span className="mono">{fmtId(p.put_id)}</span></td>
                  <td>{Math.round(p.x_rd)}, {Math.round(p.y_rd)}</td>
                  {/* Status en diepte komen afgeleid uit de backend
                      (boorstatus.py): gemeten gaat voor opgegeven, en bij een
                      put die nog geboord moet worden staat het maximum. */}
                  <td>
                    <span className={`boorstatus-stip ${p.boorstatus_code}`} />
                    <span style={p.boorstatus_code === 'onbekend' ? { color: 'var(--text-muted)' } : undefined}>
                      {p.boorstatus}
                    </span>
                  </td>
                  <td>{dieptetekst(p)}</td>
                  <td>{p.diepte_bv_filter_m ?? '—'}</td>
                  <td>{p.perceel || '—'}</td>
                  <td style={{ fontSize: 13 }}>
                    {/* Toont de lijst een recht dat nog moet ingaan, dan geldt
                        één datum voor alle regels; die staat boven de tabel. */}
                    {p.aankomend_vanaf ? (
                      <span style={{ color: 'var(--text-soft)' }}>
                        {fmtDatumNL(p.aankomend_vanaf)}
                      </span>
                    ) : p.actief ? (
                      (() => {
                        const today = new Date().toISOString().slice(0, 10);
                        /* aaneengesloten_sinds = het begin van de huidige
                           ononderbroken aanwezigheid. Niet de begindatum van de
                           laatste koppelrij (elke melding vernieuwt die, dus dan
                           lijkt een put van tien jaar oud nieuw) en niet de
                           vroegste ooit (dan telt hij over een gat heen). */
                        const isToekomst = p.aaneengesloten_sinds > today;
                        if (p.buiten_gebruik_datum) {
                          return (
                            <span className="chip" style={{ background: '#fdecea', color: '#c0392b', borderColor: '#f5b7b1' }}>
                              Afgedicht op {fmtDatumNL(p.buiten_gebruik_datum)}
                            </span>
                          );
                        }
                        return isToekomst ? (
                          <span className="chip" style={{ background: '#e3f2fd', color: '#0277bd', borderColor: '#90caf9' }}>
                            Actief vanaf {fmtDatumNL(p.aaneengesloten_sinds)}
                          </span>
                        ) : (
                          <span className="chip chip-success">Actief sinds {fmtDatumNL(p.aaneengesloten_sinds)}</span>
                        );
                      })()
                    ) : (
                      <span className="chip" style={{ color: 'var(--text-soft)' }}>
                        {fmtDatumNL(p.begin_datum_koppeling)} → {p.eind_datum_koppeling ? fmtDatumNL(p.eind_datum_koppeling) : '—'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </div>

      <div className="card">
        {/* card-header: vaste titel, melding-vervang-flow zit nu in modal */}
        <div className="card-header">
          <h3 className="card-title">
            {inrichting.rechtstype === 'vergunning' ? 'Vergunning' : 'Melding'}
          </h3>
        </div>

        {(
          /* Normaal: huidige inrichting-gegevens, evt drag-zone rechts naast veldenkolom */
          (() => {
            return (
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: 24,
                alignItems: 'start',
              }}>
                <div className="field-grid">
                  {!editing && (
                    <div className="field">
                      <span className="field-label">{inrichting.rechtstype === 'vergunning' ? 'Vergunninghouder' : 'Melder'}</span>
                      <span className="field-value">
                        {houder ? (
                          <>
                            <Link to={`/beregening/houders/${houder.houder_id}`} style={{ fontWeight: 600 }}>
                              {houder.naam || `${houder.voornaam || ''} ${houder.achternaam || ''}`.trim()}
                            </Link>
                            <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
                              {[houder.adres, houder.plaats].filter(Boolean).join(', ') || '—'}
                            </div>
                          </>
                        ) : '—'}
                      </span>
                    </div>
                  )}
                  <Field
                    label={inrichting.rechtstype === 'vergunning' ? 'Besluitnummer' : 'Verzoeknummer'}
                    value={form.verzoeknummer} editing={editing} onChange={(v) => update('verzoeknummer', v)}
                  />
                  {editing && (
                    <Field label="Rechtstype" value={form.rechtstype} editing={editing} onChange={(v) => update('rechtstype', v)}
                      options={[{value:'melding',label:'Melding'},{value:'vergunning',label:'Vergunning'}]} />
                  )}
                  <Field label="Ingangsdatum" value={form.ingangsdatum} editing={editing} onChange={(v) => update('ingangsdatum', v)} type="date" />
                  {(!isActief || editing) && (
                    <Field label="Einddatum" value={form.einddatum} editing={false} onChange={() => {}} />
                  )}
                  {/* Doel-veld: bij melding simpele select met nieuwe labels, bij vergunning chip-lijst (lees) of multi-select-cards (bewerk, hieronder buiten dit grid) */}
                  {form.rechtstype === 'melding' ? (
                    /* Niet via Field, omdat hier onder de keuze de volledige
                       opsomming hoort te staan. Die past niet in de keuzelijst
                       zelf — "Akkerbouw, vollegrondstuinbouw, …" wordt daar
                       afgekapt — en zonder die regel moet de behandelaar maar
                       gokken wat de melder heeft aangevinkt. */
                    <div className="field">
                      <span className="field-label">Doel</span>
                      {editing ? (
                        <>
                          <select value={form.doel || ''} onChange={(e) => {
                            const v = e.target.value;
                            // Sync ook doelen[] zodat backend correcte array krijgt
                            setForm({ ...form, doel: v, doelen: v ? [v] : [] });
                          }}>
                            <option value="">—</option>
                            {MELDING_DOELEN.map((d) => (
                              <option key={d.waarde} value={d.waarde}>{d.kort}</option>
                            ))}
                          </select>
                          {form.doel && (
                            <span className="veld-toelichting">
                              {`${doelVoluit(form.doel)}.`}
                              {MELDING_DOEL_TOELICHTING[form.doel]
                                ? ` ${MELDING_DOEL_TOELICHTING[form.doel]}`
                                : ''}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className={`field-value ${!form.doel ? 'empty' : ''}`}
                              title={form.doel ? doelVoluit(form.doel) : undefined}>
                          {form.doel ? labelVoorDoel(form.doel) : '—'}
                        </span>
                      )}
                    </div>
                  ) : (
                    !editing && (
                      <div className="field doelen-chip-lijst">
                        <span className="field-label">Doelen</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                          {(form.doelen && form.doelen.length > 0) ? (
                            form.doelen.map(d => (
                              <span key={d} style={{
                                background: '#eef2f7', color: '#34495e',
                                padding: '4px 10px', borderRadius: 12,
                                fontSize: 12, fontWeight: 500,
                              }}>{labelVoorDoel(d)}</span>
                            ))
                          ) : (
                            <span style={{ color: 'var(--text-muted)' }}>—</span>
                          )}
                        </div>
                      </div>
                    )
                  )}
                  {/* Max pompcapaciteit alleen bij melding */}
                  {form.rechtstype === 'melding' && (
                    <Field label="Max pompcapaciteit (m³/u)" value={form.max_pompcap_m3_uur} editing={editing} onChange={(v) => update('max_pompcap_m3_uur', v)} type="number" />
                  )}
                  {/* Bij vergunning in lees-modus: vier debiet-velden tonen */}
                  {form.rechtstype === 'vergunning' && !editing && (
                    <>
                      <Field label="Vergund uurdebiet (m³)" value={form.vergund_debiet_uur} editing={false} onChange={() => {}} />
                      <Field label="Vergund maanddebiet (m³)" value={form.vergund_debiet_maand} editing={false} onChange={() => {}} />
                      <Field label="Vergund kwartaaldebiet (m³)" value={form.vergund_debiet_kwartaal} editing={false} onChange={() => {}} />
                      <Field label="Vergund jaardebiet (m³)" value={form.vergund_debiet_jaar} editing={false} onChange={() => {}} />
                    </>
                  )}
                  <Field label="Bedrijfsomvang (ha)" value={form.bedrijfsomvang_ha} editing={editing} onChange={(v) => update('bedrijfsomvang_ha', v)} type="number" />
                </div>

                {/* Vergunning-specifieke edit-UI: doelen + debiet */}
                {form.rechtstype === 'vergunning' && editing && (
                  <div style={{ marginTop: 16, padding: '16px 0', borderTop: '1px solid var(--separator)' }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-soft)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Doelen vergunning</h4>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
                      Optioneel. Selecteer eerst de hoofdcategorieen, daarna de bijbehorende sub-doelen.
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                      {VERGUNNING_CATEGORIEEN.map(cat => {
                        const huidigeCategorieen = categorieenVanDoelen(form.doelen);
                        const checked = huidigeCategorieen.includes(cat.key);
                        return (
                          <label key={cat.key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                const verwijderde_values = checked ? cat.opties.map(o => o.value) : [];
                                const nieuwe_doelen = checked
                                  ? (form.doelen || []).filter(d => !verwijderde_values.includes(d))
                                  : (form.doelen || []);
                                setForm({ ...form, doelen: nieuwe_doelen });
                              }}
                            />
                            <span style={{ fontWeight: 500 }}>{cat.label}</span>
                          </label>
                        );
                      })}
                    </div>
                    {(() => {
                      const huidigeCategorieen = categorieenVanDoelen(form.doelen);
                      // Toon ook sub-doelen-blok voor categorieën waarvoor nog niets aangevinkt is, mits aangevinkt
                      // Maar via categorieenVanDoelen werkt het pas als er minstens 1 doel binnen die categorie is
                      // Voor UX: toon sub-doelen voor *alle* categorieën waarvan hoofdcat aangevinkt is
                      // We hebben hier alleen huidigeCategorieen afgeleid uit doelen. Voor nieuwe categorieën
                      // zonder doel moeten we de checkbox-status verkrijgen. Simpel: toon sub-opties van álle
                      // categorieën waarvan de hoofdcategorie is aangevinkt; aangevinkt = staat in huidigeCategorieen.
                      const zichtbareCategorieen = VERGUNNING_CATEGORIEEN.filter(cat => huidigeCategorieen.includes(cat.key));
                      if (zichtbareCategorieen.length === 0) return null;
                      return (
                        <div style={{ borderTop: '1px solid var(--separator)', paddingTop: 12 }}>
                          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>Sub-doelen</div>
                          {zichtbareCategorieen.map(cat => (
                            <div key={cat.key} style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>{cat.label}</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 8 }}>
                                {cat.opties.map(opt => {
                                  const checked = (form.doelen || []).includes(opt.value);
                                  return (
                                    <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => {
                                          const nieuwe_doelen = checked
                                            ? (form.doelen || []).filter(d => d !== opt.value)
                                            : [...(form.doelen || []), opt.value];
                                          setForm({ ...form, doelen: nieuwe_doelen });
                                        }}
                                      />
                                      <span>{opt.label}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-soft)', marginTop: 24, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Vergund maximum debiet</h4>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
                      Alle vier optioneel. Vul in wat in de beschikking staat.
                    </div>
                    <div className="field-grid">
                      <Field label="Vergund uurdebiet (m³)" value={form.vergund_debiet_uur} editing={true} onChange={(v) => update('vergund_debiet_uur', v)} type="number" />
                      <Field label="Vergund maanddebiet (m³)" value={form.vergund_debiet_maand} editing={true} onChange={(v) => update('vergund_debiet_maand', v)} type="number" />
                      <Field label="Vergund kwartaaldebiet (m³)" value={form.vergund_debiet_kwartaal} editing={true} onChange={(v) => update('vergund_debiet_kwartaal', v)} type="number" />
                      <Field label="Vergund jaardebiet (m³)" value={form.vergund_debiet_jaar} editing={true} onChange={(v) => update('vergund_debiet_jaar', v)} type="number" />
                    </div>
                  </div>
                )}

              </div>
            );
          })()
        )}
      </div>

      {!historieModus && (
        <GrondwaterproductieDossier inrichtingId={id} />
      )}

      {(historie.length > 1 || klaarstaand.length > 0 || nogNietIngegaanRecht) && !historieModus && (
        <div className="card">
          <div className="card-header" style={{ marginBottom: 8 }}>
            <h3 className="card-title">
              Gebruiksrechten <span className="chip" style={{ marginLeft: 8 }}>{historie.length}</span>
            </h3>
          </div>
          <div className="tijdlijn">
            {/* Wat nog niet geldt staat bovenaan, met een oranje stip: het is
                wél vastgelegd, maar het register toont het pas op zijn datum.

                Een inrichting die bij het klaarzetten is aangemaakt heeft het
                recht van díe melding al in de rij staan. Dan gaat het over
                hetzelfde en hoort het er één keer te staan — de klaarstaande
                versie, want die kun je openen en intrekken. */}
            {klaarstaand.map((k) => (
              <div className="tijdlijn-node" key={k.sleutel}>
                <span className="tijdlijn-dot toekomstig" />
                <div
                  className="tijdlijn-inhoud klikbaar"
                  onClick={k.soort === 'melding'
                    ? () => navigate(`/beregening/inrichtingen/${id}/gepland-melding/${k.gepland_id}`)
                    : () => navigate(`/beregening/inrichtingen/${id}/gepland-besluit/${k.gepland_id}`)}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>
                      <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>
                        {k.soort === 'melding' ? kenmerkTypeLabel(k.kenmerk_type) : 'Wijzigingsbesluit'}{' '}
                      </span>
                      <span className="mono">{k.kenmerk || '—'}</span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                      Vanaf {fmtDatumNL(k.datum)}
                    </div>
                  </div>
                  <span className="tijdlijn-rechts">
                    <span className="tijdlijn-badge toekomstig">Toekomstige staat</span>
                    <span className="tijdlijn-chevron">›</span>
                  </span>
                </div>
              </div>
            ))}
            {historie.map((slice, idx) => {
              /* Ook het lopende recht: dat is net zo goed een periode met
                 eigen putten en eigen gegevens, en juist die wil je kunnen
                 bekijken zonder de aankomende putten ertussen. */
              const klikbaar = true;
              /* Een recht dat al vastligt maar pas later ingaat: geen "Huidige
                 staat", want dat is het nog niet. */
              const nogNietIngegaan = slice.is_huidig
                && slice.inwerkingtreding && slice.inwerkingtreding > vandaag;
              const alBovenaan = nogNietIngegaan && klaarstaand.some(
                (k) => k.soort === 'melding' && k.kenmerk === slice.kenmerk);
              if (alBovenaan) return null;
              return (
                <div className="tijdlijn-node" key={slice.kenmerk || idx}>
                  <span className={`tijdlijn-dot${nogNietIngegaan ? ' toekomstig' : (slice.is_huidig ? ' huidig' : '')}`} />
                  <div
                    className={`tijdlijn-inhoud${klikbaar ? ' klikbaar' : ''}`}
                    onClick={klikbaar ? () => navigate(`/beregening/inrichtingen/${id}/historie/${slice.periode_id}`) : undefined}
                  >
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>
                        <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>{kenmerkTypeLabel(slice.kenmerk_type)} </span>
                        <span className="mono">{slice.kenmerk || '—'}</span>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                        {/* "t/m heden" klopt niet voor een recht dat nog moet
                            beginnen; dan alleen de dag waarop het ingaat. */}
                        {nogNietIngegaan
                          ? `Vanaf ${fmtDatumNL(slice.inwerkingtreding)}`
                          : fmtPeriode(slice.inwerkingtreding, slice.uitwerkingtreding)}
                      </div>
                    </div>
                    <span className="tijdlijn-rechts">
                      {nogNietIngegaan
                        ? <span className="tijdlijn-badge toekomstig">Toekomstige staat</span>
                        : slice.is_huidig
                          ? <span className="tijdlijn-badge">Huidige staat</span>
                          : null}
                      {klikbaar && <span className="tijdlijn-chevron">›</span>}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

        </div>

        {!historieModus && (
          <aside className="detail-aside">
            <OpmerkingenVeld
              initialValue={form.opmerkingen}
              onSave={async (tekst) => {
                await updateInrichtingOpmerkingen(id, tekst || null);
                update('opmerkingen', tekst);
                setData(prev => prev ? { ...prev, inrichting: { ...prev.inrichting, opmerkingen: tekst || null } } : prev);
              }}
            />
          </aside>
        )}
      </div>

      <Modal
        isOpen={mailDialogOpen}
        onClose={() => setMailDialogOpen(false)}
        title="Bijwerkings-mail versturen"
      >
        <p>Verstuur een mail met de actuele registratie naar:</p>
        <p style={{ fontSize: 16, fontWeight: 600, margin: '8px 0' }}>{houder?.emailadres}</p>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>
          De mail bevat de huidige stand van inrichting {fmtId(id)} (houder, rechtsgrondslag, putten).
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={() => setMailDialogOpen(false)}>Annuleren</button>
          <button className="btn btn-primary" onClick={verstuurMail}>Verstuur mail</button>
        </div>
      </Modal>

      <Modal
        isOpen={prodOpen}
        onClose={() => setProdOpen(false)}
        title="Productieopgave opvragen"
      >
        <p style={{ marginTop: 0, color: 'var(--text-soft)' }}>
          De houder krijgt een e-mail met een beveiligde link om de kwartaalcijfers (m³) van het opgegeven jaar door te geven. De link is 30 dagen geldig en kan één keer worden gebruikt.
        </p>
        <div className="field" style={{ marginTop: 12 }}>
          <label className="field-label">Opgavejaar</label>
          <input
            type="number"
            value={prodJaar}
            min="2000"
            max={new Date().getFullYear()}
            onChange={(e) => setProdJaar(parseInt(e.target.value, 10) || prodJaar)}
            style={{ width: 120 }}
          />
        </div>

        {prodStatus === null ? (
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '12px 2px 0' }}>Bezig met controleren…</p>
        ) : prodStatus._fout ? (
          <p style={{ fontSize: 13, color: '#dc2626', margin: '12px 2px 0' }}>Kon de status niet ophalen.</p>
        ) : prodStatus.ontbrekende_kwartalen.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '12px 2px 0' }}>
            Voor {prodJaar} zijn alle kwartaalcijfers al bekend. Er valt niets op te vragen.
          </p>
        ) : (
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '12px 2px 0' }}>
            Nog op te vragen voor {prodJaar}: kwartaal {prodStatus.ontbrekende_kwartalen.join(', ')}. De uitnodiging gaat naar <strong>{houder?.emailadres}</strong>.
          </p>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={() => setProdOpen(false)}>Annuleren</button>
          <button
            className="btn btn-primary"
            onClick={verstuurProductie}
            disabled={prodBezig || !prodStatus || prodStatus._fout || prodStatus.ontbrekende_kwartalen.length === 0}
          >
            {prodBezig ? 'Versturen…' : 'Uitnodiging versturen'}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={!!prodResult}
        onClose={() => setProdResult(null)}
        title={prodResult?.ok ? 'Uitnodiging verzonden' : 'Versturen mislukt'}
      >
        {prodResult?.ok ? (
          <div>
            <p style={{ marginTop: 0 }}>De productieopgave voor {prodResult.jaar} is verstuurd naar:</p>
            <p style={{ fontSize: 15, fontWeight: 600, margin: '8px 0' }}>{prodResult.ontvanger}</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
              Opgevraagde kwartalen: {prodResult.ontbrekende_kwartalen?.join(', ')}. De link is 30 dagen geldig.
            </p>
          </div>
        ) : (
          <div>
            <p style={{ marginTop: 0 }}>Er ging iets mis bij het versturen.</p>
            <p style={{ fontSize: 13, color: '#dc2626', fontFamily: 'monospace', margin: '8px 0' }}>{prodResult?.error}</p>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-primary" onClick={() => setProdResult(null)}>Sluiten</button>
        </div>
      </Modal>

      <Modal
        isOpen={vwbOpen}
        onClose={() => setVwbOpen(false)}
        title="Veldwerkgegevens opvragen"
      >
        <p style={{ marginTop: 0, color: 'var(--text-soft)' }}>
          De houder krijgt één e-mail met een beveiligde link voor alle putten van deze inrichting waarvan nog gegevens ontbreken (diepte, bovenkant filter, aanlegdatum). De link is 30 dagen geldig en kan meerdere keren worden gebruikt: wie iets nog niet weet, komt later terug.
        </p>

        {vwbStatus === null ? (
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '12px 2px 0' }}>Bezig met controleren…</p>
        ) : vwbStatus._fout ? (
          <p style={{ fontSize: 13, color: '#dc2626', margin: '12px 2px 0' }}>Kon de status niet ophalen.</p>
        ) : vwbStatus.aantal_openstaand === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '12px 2px 0' }}>
            Van alle putten van deze inrichting zijn de gegevens al bekend. Er valt niets op te vragen.
          </p>
        ) : (
          <>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '12px 2px 0' }}>
              Nog op te vragen: <strong>{vwbStatus.aantal_openstaand}</strong> {vwbStatus.aantal_openstaand === 1 ? 'put' : 'putten'} ({vwbStatus.putten.join(', ')}). De uitnodiging gaat naar <strong>{houder?.emailadres}</strong>.
            </p>
            {vwbStatus.lopende_link && (
              <p style={{ fontSize: 13, color: '#8a6100', margin: '8px 2px 0' }}>
                Er loopt al een link, verstuurd op {fmtDatumNL(vwbStatus.lopende_link.verzonden_op)} naar {vwbStatus.lopende_link.verzonden_naar}. Een nieuwe link maakt de oude niet ongeldig.
              </p>
            )}
          </>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={() => setVwbOpen(false)}>Annuleren</button>
          <button
            className="btn btn-primary"
            onClick={verstuurVeldwerkBulk}
            disabled={vwbBezig || !vwbStatus || vwbStatus._fout || vwbStatus.aantal_openstaand === 0}
          >
            {vwbBezig ? 'Versturen…' : 'Uitnodiging versturen'}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={!!vwbResult}
        onClose={() => setVwbResult(null)}
        title={vwbResult?.ok ? 'Uitnodiging verzonden' : 'Versturen mislukt'}
      >
        {vwbResult?.ok ? (
          <div>
            <p style={{ marginTop: 0 }}>De uitnodiging is verstuurd naar:</p>
            <p style={{ fontSize: 15, fontWeight: 600, margin: '8px 0' }}>{vwbResult.ontvanger}</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
              Opgevraagd: {vwbResult.aantal_openstaand} {vwbResult.aantal_openstaand === 1 ? 'put' : 'putten'}. De link is 30 dagen geldig.
            </p>
          </div>
        ) : (
          <div>
            <p style={{ marginTop: 0 }}>Er ging iets mis bij het versturen.</p>
            <p style={{ fontSize: 13, color: '#dc2626', fontFamily: 'monospace', margin: '8px 0' }}>{vwbResult?.error}</p>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-primary" onClick={() => setVwbResult(null)}>Sluiten</button>
        </div>
      </Modal>

      <Modal
        isOpen={!!mailResult}
        onClose={() => setMailResult(null)}
        title={mailResult?.ok ? 'Mail verzonden' : 'Mail mislukt'}
      >
        {mailResult?.ok ? (
          <>
            <p>De mail is succesvol verzonden naar:</p>
            <p style={{ fontSize: 15, fontWeight: 600, margin: '8px 0' }}>{mailResult.ontvanger}</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Onderwerp: {mailResult.onderwerp}</p>
          </>
        ) : (
          <>
            <p>Er ging iets mis bij het versturen:</p>
            <p style={{ fontSize: 13, color: '#dc2626', fontFamily: 'monospace', margin: '8px 0' }}>{mailResult?.error}</p>
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-primary" onClick={() => setMailResult(null)}>Sluiten</button>
        </div>
      </Modal>

      <Modal
        isOpen={beeindigOpen}
        onClose={() => setBeeindigOpen(false)}
        title="Inrichting beëindigen"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setBeeindigOpen(false)}>Annuleren</button>
            <button className="btn btn-danger" onClick={beeindigen}>Beëindigen</button>
          </>
        }
      >
        <p style={{ marginTop: 0, marginBottom: 20, color: 'var(--text-soft)' }}>
          Bij beëindigen wordt de inrichting gearchiveerd en alle koppelingen aan putten worden gesloten.
          De putten zelf blijven bestaan.
        </p>
        <div className="field">
          <label className="field-label">Einddatum</label>
          <input type="date" value={beeindigDatum} onChange={(e) => setBeeindigDatum(e.target.value)} />
        </div>
      </Modal>

      <CorrespondentieModal
        isOpen={publiekOpen}
        onClose={() => setPubliekOpen(false)}
        inrichtingId={id}
        /* Het Melding-document hoort alleen bij een melding. Bij een vergunning
           zonder klaarstaande melding valt er niets te tonen, en dan wordt de
           kans op een document ook niet opgevraagd. */
        toonDocument={inrichting.rechtstype === 'melding' || geplandeMeldingen.length > 0}
      />

      {/* Geneste route: het venster verschijnt hierboven, de inrichting blijft
          eronder staan. `herlaad` erbij, zodat een venster dat iets intrekt de
          pagina eronder kan bijwerken in plaats van een verouderde tijdlijn
          achter te laten. */}
      <Outlet context={{ herlaad: laad }} />
    </div>
  );
}
